import {useEffect, useRef, useState} from 'react';

import {getConfigData} from '@udemy/shared-utils';
import {Price} from '@udemy/shopping';
import {udApi} from '@udemy/ud-api';

export interface CoursePrice {
    list_price: Price;
    purchase_price: Price;
    saving_price?: Price;
    current_discount?: {
        code?: string;
        is_public?: boolean;
        campaign?: {show_code: boolean};
    };
    price_serve_tracking_id: string;
}

/**
 * Fetches list and purchase price for a single course via the /pricing/ REST endpoint.
 * Pass null to skip the fetch (e.g. when the course ID is not yet known).
 */
export function useCoursePrice(courseId: number | null): {
    price: CoursePrice | null;
    isLoading: boolean;
} {
    const [price, setPrice] = useState<CoursePrice | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const currentCourseIdRef = useRef(courseId);

    useEffect(
        function fetchCoursePrice() {
            currentCourseIdRef.current = courseId;

            if (!courseId || courseId < 0) {
                setPrice(null);
                setIsLoading(false);
                return;
            }

            const fetchPrice = async () => {
                setIsLoading(true);
                try {
                    const udConfig = getConfigData();
                    const currency = udConfig?.price_country?.currency;
                    const params: Record<string, string> = {
                        course_ids: String(courseId),
                        'fields[pricing_result]':
                            'price,list_price,saving_price,discount_price,discount,price_serve_tracking_id,campaign',
                    };
                    if (currency) {
                        params.currency = currency;
                    }

                    const response = await udApi.get('/pricing/', {params});

                    if (currentCourseIdRef.current !== courseId) return;

                    const courseData = response.data?.courses?.[courseId];
                    if (courseData) {
                        setPrice({
                            list_price: courseData.list_price,
                            saving_price: courseData.saving_price,
                            purchase_price: courseData.price,
                            price_serve_tracking_id: courseData.price_serve_tracking_id,
                            ...(courseData.campaign
                                ? {
                                      current_discount: {
                                          code: courseData.campaign.code,
                                          is_public: courseData.campaign.is_public,
                                          campaign: courseData.campaign,
                                      },
                                  }
                                : {}),
                        });
                    } else {
                        setPrice(null);
                    }
                } catch {
                    if (currentCourseIdRef.current === courseId) {
                        setPrice(null);
                    }
                } finally {
                    if (currentCourseIdRef.current === courseId) {
                        setIsLoading(false);
                    }
                }
            };

            fetchPrice();
        },
        [courseId],
    );

    return {price, isLoading};
}
